import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import { FiPlus, FiSearch, FiArchive, FiMessageSquare } from 'react-icons/fi';
import dayjs from 'dayjs';

const ChatSidebar = ({ onCreateChat, isTrainer, userId }) => {
    const { t } = useTranslation('chat');
    const {
        chatRooms,
        archivedChatRooms,
        activeChatRoom,
        openChatRoom,
        fetchArchivedChatRooms
    } = useContext(ChatContext);

    const [searchQuery, setSearchQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);

    const filteredRooms = (showArchived ? archivedChatRooms : chatRooms).filter(room =>
        room.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleToggleArchived = () => {
        if (!showArchived) {
            fetchArchivedChatRooms();
        }
        setShowArchived(!showArchived);
    };

    const formatLastMessage = (message) => {
        if (!message) return t('noMessagesLabel');
        if (message.status === 'DELETED') return t('messageDeletedLabel');
        const name = message.senderFirstName || '';
        const content = message.content?.length > 30
            ? message.content.substring(0, 30) + '...'
            : message.content;
        return `${name}: ${content}`;
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = dayjs(dateString);
        const now = dayjs();
        if (date.isSame(now, 'day')) {
            return date.format('HH:mm');
        }
        if (date.isSame(now.subtract(1, 'day'), 'day')) {
            return t('yesterday');
        }
        return date.format('DD.MM');
    };

    const getChatTypeIcon = (type) => {
        switch (type) {
            case 'SCHOOL_ANNOUNCEMENT': return '📢';
            case 'TRAINING_GROUP': return '🐕';
            case 'TRAINER_CHAT': return '👥';
            case 'CUSTOM_GROUP': return '💬';
            default: return '✉️';
        }
    };

    return (
        <div className="chat-sidebar">
            <div className="chat-sidebar-header">
                <h2>{showArchived ? t('archived') : t('title')}</h2>
                <div className="chat-sidebar-actions">
                    <button
                        className="chat-sidebar-btn"
                        onClick={handleToggleArchived}
                        title={showArchived ? t('activeChats') : t('archived')}
                    >
                        {showArchived ? <FiMessageSquare /> : <FiArchive />}
                    </button>
                    {!showArchived && (
                        <button
                            className="chat-sidebar-btn chat-sidebar-btn-primary"
                            onClick={() => onCreateChat(null)}
                            title={t('newChat')}
                        >
                            <FiPlus />
                        </button>
                    )}
                </div>
            </div>

            <div className="chat-search">
                <FiSearch className="chat-search-icon" />
                <input
                    type="text"
                    placeholder={t('search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="chat-search-input"
                />
            </div>

            <div className="chat-rooms-list">
                {filteredRooms.length === 0 ? (
                    <div className="chat-no-rooms">
                        <p>{showArchived ? t('noArchivedChats') : t('noChats')}</p>
                        {!showArchived && (
                            <button
                                className="chat-create-first-btn"
                                onClick={() => onCreateChat(null)}
                            >
                                {t('startConversation')}
                            </button>
                        )}
                    </div>
                ) : (
                    filteredRooms.map(room => (
                        <div
                            key={room.id}
                            className={`chat-room-item ${activeChatRoom?.id === room.id ? 'active' : ''} ${room.status === 'FINISHED' ? 'finished' : ''}`}
                            onClick={() => openChatRoom(room.id)}
                        >
                            <div className="chat-room-item-avatar">
                                <span>{getChatTypeIcon(room.type)}</span>
                            </div>
                            <div className="chat-room-item-content">
                                <div className="chat-room-item-header">
                                    <span className="chat-room-item-name">{room.name}</span>
                                    <span className="chat-room-item-time">
                                        {formatTime(room.lastMessage?.createdAt || room.updatedAt)}
                                    </span>
                                </div>
                                <div className="chat-room-item-preview">
                                    <span className="chat-room-item-message">
                                        {formatLastMessage(room.lastMessage)}
                                    </span>
                                    {room.unreadCount > 0 && (
                                        <span className="chat-unread-badge">{room.unreadCount}</span>
                                    )}
                                </div>
                                <span className="chat-room-type-label">
                                    {t(`roomTypes.${room.type}`, { defaultValue: room.type })}
                                    {room.status === 'FINISHED' && ` (${t('finished')})`}
                                    {room.muted && <span className="chat-room-muted">🔇</span>}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default ChatSidebar;
